import React from 'react';
import { Box, Typography } from '@mui/material';
import { useWorkspace } from './WorkspaceContext.js';
import { ACTION_META, ACTION_TYPES, DANGER_CLASS_STYLES } from './workspaceConstants.js';

const SwatchRow = ({ color, label, border = '1px solid #666' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
    <Box sx={{ width: '14px', height: '14px', background: color, border, flexShrink: 0 }} />
    <span>{label}</span>
  </Box>
);

const RampRow = ({ gradient, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
    <Box sx={{ width: '60px', height: '10px', background: gradient, border: '1px solid #666', flexShrink: 0 }} />
    <span>{label}</span>
  </Box>
);

const Heading = ({ children, first }) => (
  <Typography sx={{ fontWeight: 'bold', mt: first ? 0 : '8px', mb: '3px', fontSize: '13px' }}>
    {children}
  </Typography>
);

const WATER_LEVELS = [
  { color: '#B3DDF2', label: '5–10 cm' },
  { color: '#73B3E7', label: '10–30 cm' },
  { color: '#2A6CB3', label: '30–50 cm' },
  { color: '#7D3C98', label: '> 50 cm' },
];

const DISCHARGES = [
  { color: '#2ECC71', label: '0,01–0,05 m³/s' },
  { color: '#17A589', label: '0,05–0,1 m³/s' },
  { color: '#F39C12', label: '0,1–1 m³/s' },
  { color: '#6E2C00', label: '> 1 m³/s' },
];

const DANGER_CLASSES = [
  { dangerClass: 0, label: 'keine' },
  { dangerClass: 1, label: 'gering' },
  { dangerClass: 2, label: 'mittel' },
  { dangerClass: 3, label: 'hoch' },
  { dangerClass: 4, label: 'sehr hoch' },
];

// Floating legend in the bottom right of the map, showing only the sections
// whose layer is currently switched on.
const MapLegend = () => {
  const { layers, activeScenario, actions } = useWorkspace();

  const hasScenario = Boolean(activeScenario);
  const computed = hasScenario && activeScenario.status === 'done';

  const showBuildings = layers.buildings && computed;
  const showWater = hasScenario && layers.waterLevel;
  const showDischarge = hasScenario && layers.discharge;
  const showAnimated = hasScenario && layers.flowPathsAnimated;
  const showStatic = hasScenario && layers.flowPathsStatic;
  const visibleActionTypes = hasScenario && layers.actions
    ? ACTION_TYPES.filter((type) => layers.actionTypes[type] && actions[type].length > 0)
    : [];

  const sections = [showBuildings, showWater, showDischarge, showAnimated, showStatic,
    visibleActionTypes.length > 0];
  if (!sections.some(Boolean)) return null;

  let isFirst = true;
  const heading = (text) => {
    const node = <Heading first={isFirst}>{text}</Heading>;
    isFirst = false;
    return node;
  };

  return (
    <Box sx={{
      position: 'absolute', bottom: '24px', right: '12px', zIndex: 900,
      background: 'rgba(255,255,255,0.92)', padding: '10px 14px',
      borderRadius: '5px', border: '1px solid #aaa',
      fontSize: '13px', lineHeight: 1.6, pointerEvents: 'none',
      maxHeight: 'calc(100% - 60px)', overflowY: 'auto',
    }}>
      {showBuildings && (<>
        {heading('Gefährdung der Gebäude')}
        {DANGER_CLASSES.map(({ dangerClass, label }) => (
          <SwatchRow
            key={label}
            color={DANGER_CLASS_STYLES[dangerClass].fillColor}
            label={label}
          />
        ))}
      </>)}

      {showWater && (<>
        {heading('Wasserstand')}
        {WATER_LEVELS.map(({ color, label }) => <SwatchRow key={label} color={color} label={label} />)}
        {computed && (
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: '2px', maxWidth: '170px', lineHeight: 1.3 }}>
            Klick in die Karte zeigt den genauen Wert.
          </Typography>
        )}
      </>)}

      {showDischarge && (<>
        {heading('Abfluss')}
        {DISCHARGES.map(({ color, label }) => <SwatchRow key={label} color={color} label={label} />)}
      </>)}

      {showAnimated && (<>
        {heading('Fließwege (animiert)')}
        <RampRow
          gradient="linear-gradient(to right, #FFFF00, #FFA500, #FF4500, #8B0000)"
          label="langsam → schnell"
        />
      </>)}

      {showStatic && (<>
        {heading('Fließwege (statisch)')}
        {/* Grey ramp matches ACCUMULATION_MASK_CLASSES in GeoTiffMapServer. */}
        <RampRow
          gradient="linear-gradient(to right, #D9D9D9, #B0B0B0, #878787, #5E5E5E, #353535, #111111)"
          label="kleines → großes Einzugsgebiet"
        />
      </>)}

      {visibleActionTypes.length > 0 && (<>
        {heading('Maßnahmen')}
        {visibleActionTypes.map((type) => (
          <SwatchRow key={type} color={ACTION_META[type].color} label={ACTION_META[type].label} />
        ))}
      </>)}
    </Box>
  );
};

export default MapLegend;
